/**
 * Iter 70.4 (2026-05-18) — Long-format date picker for contract forms.
 *
 * Custom popover (NO Radix Popover). React 19 + WebKit testing established
 * that Radix Popover trigger composition silently dropped our React onClick
 * handler on Safari, so this component now uses:
 *   • a plain <Button onClick> trigger toggling a useState boolean
 *   • a sibling absolutely-positioned <div> rendering the Calendar
 *   • document mousedown/touchstart listener for click-outside-to-close
 *   • Escape keydown listener
 *
 * react-day-picker (via ui/calendar) is retained for the calendar grid.
 *
 * Hard rules (do not re-introduce):
 *   • NO Radix Popover/PopoverTrigger/PopoverAnchor/PopoverContent imports
 *   • NO asChild anywhere on the trigger
 *   • NO @radix-ui/react-slot composition on the trigger
 *   • NO onPointerDown stopPropagation
 */
import { useEffect, useRef, useState } from 'react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { CalendarIcon, X } from 'lucide-react';
import { formatDateLong } from '../lib/dateFormat';

function isoToDate(iso) {
  if (!iso) return undefined;
  // YYYY-MM-DD → Date at local-midnight (avoid TZ-shift jump)
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function dateToIso(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function ContractDatePicker({
  value,
  onChange,
  placeholder = 'Select a date',
  disabled = false,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const selected = isoToDate(value);

  // Outside-click + Escape close (only when open).
  useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (e) => {
      const root = containerRef.current;
      if (!root) return;
      if (root.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocPointer, true);
    document.addEventListener('touchstart', onDocPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer, true);
      document.removeEventListener('touchstart', onDocPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-block w-full">
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        data-testid={testId || 'contract-date-picker'}
        style={{ touchAction: 'manipulation' }}
        className="w-full justify-start text-left font-normal h-9 px-3"
      >
        <CalendarIcon className="mr-2 h-4 w-4 opacity-60" />
        {selected ? (
          <span>{formatDateLong(value)}</span>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        {selected && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange('');
            }}
            className="ml-auto opacity-60 hover:opacity-100"
            data-testid={`${testId || 'date'}-clear`}
          >
            <X className="h-3.5 w-3.5" />
          </span>
        )}
      </Button>
      {open && (
        <div
          role="dialog"
          aria-label="Choose date"
          className="absolute top-full left-0 mt-1 z-[100] w-auto p-0 bg-popover border border-border/40 shadow-xl rounded-md"
          style={{ minWidth: 'max-content' }}
        >
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => {
              onChange(dateToIso(d));
              setOpen(false);
            }}
            captionLayout="dropdown"
            fromYear={2020}
            toYear={2035}
            initialFocus
          />
        </div>
      )}
    </div>
  );
}
